import { computed, inject, Injectable } from '@angular/core';
import { ISalesResponse, RestApiService } from '@pc/services';
import { catchError, map, Observable, of } from 'rxjs';
import { ProductCover, Traveler } from '../../models/traveler.interface';
import { MakeQuoteBodyRequest } from '../../models/quote/make-quote';
import { MainStore } from '../../../store/main-store/main-store';
import { BasicQuoteRequest, ExcessLevel, InsuredAmount } from '../../models/quote/basic-quote.interface';
import { TravelersStore } from '../../../core/stores/travelers.store';
import { ProductsStore } from '../../../pages/covers-page/store/products.store';
import { ProcessTypeStore } from '../../../store/process-type-store/process-type-store';
import { QuoteOverviewService } from './quote-overview.service';
import {
  MakeQuoteParticipant,
  MakeQuoteResponse,
  UWresultStatus
} from '../../models/quote/make-quote-response.interface';
import { SmartlookService } from '@pc/smartlook';
import { AnimationDialogService } from '@pc/dialog-abstract';
import { Dialog } from '@angular/cdk/dialog';
import { AlertPopupComponent } from '../../../components/alert-popup/alert-popup.component';
import {
  ReferralPopupComponent,
  ReferralPopupData
} from '../../../components/health-declaration/referral-popup/referral-popup.component';
import { Translations } from '../translations.service';
import { DomSanitizer } from '@angular/platform-browser';
import { CoverType, ICover } from '../../models/cover.interface';
import { PcAlertsImages } from '@pc/common';
import { CurrencyPipe } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class MakeQuoteService {
  private restApiService = inject(RestApiService<ISalesResponse<MakeQuoteResponse>>);
  private translations = inject(Translations);
  private mainStore = inject(MainStore);
  private travelersStore = inject(TravelersStore);
  private productStore = inject(ProductsStore);
  private processTypeDataStore = inject(ProcessTypeStore);

  private quoteOverviewService = inject(QuoteOverviewService);
  private domSanitizer = inject(DomSanitizer);
  private exceptionMessage = '';

  private basicQuotePayload = computed(() => this.mainStore.basicQuotePayload());
  private allTravelers = computed(() => this.travelersStore.sortedAllTravelers());
  private products = computed(() => this.productStore.products()[0]);
  private selectedProductId = computed(() => this.productStore.selectedProductId() || 0);
  private totalPrice = computed(() => this.quoteOverviewService.quotePrice());
  private servicePhone = computed(() => this.getServicePhoneNumber(this.processTypeDataStore.isAgentProcess()));
  private smartlookService = inject(SmartlookService);

  private animationDialogService = inject(AnimationDialogService);
  private dialog = inject(Dialog);

  public makeQuoteRequest(): Observable<UWresultStatus> {
    this.clearOutdatedCovers();
    const body = this.buildMakeQuoteBody();
    return this.restApiService.post<ISalesResponse<MakeQuoteResponse>>('Quote/makeQuote', body, true).pipe(
      map((res) => {
        const response = res.data;
        const hof = this.travelersStore.adultTravelers()[0];

        this.smartlookService.setUserId(response.participants[0]?.customerID, {
          QuoteID: response.quoteID,
          LeadID: response.leadID
        });
        if (hof) {
          hof.customerID = response.participants[0]?.customerID;
          hof.personalNumId = response.participants[0]?.personalNumId;
        }

        this.quoteOverviewService.setCurrecyId(response.pricePlanCurrency);
        this.quoteOverviewService.setCurrencyISO(response.planCurrencyISO);
        this.processTypeDataStore.setLeadId(response.leadID);
        this.processTypeDataStore.setQuoteId(response.quoteID);
        this.mainStore.setMakeQuoteResponse(response);

        const uwResults = response.participants.map((p) => p.uwResult);
        if (uwResults.includes(UWresultStatus.AutoRider)) {
          this.handlePreExCharge(response.participants);
          return UWresultStatus.AutoRider;
        }

        return UWresultStatus.Success;
      }),
      catchError((error) => {
        const response = error.error.data as MakeQuoteResponse;

        this.processTypeDataStore.setLeadId(response.leadID);
        this.processTypeDataStore.setQuoteId(response.quoteID);
        this.exceptionMessage = response.exceptionMessage || '';
        this.showReferalPopup();
        this.mainStore.setIsLoading(false);
        return of(UWresultStatus.NoSale);
      })
    );
  }

  public buildMakeQuoteBody(
    allTravelers = this.allTravelers(),
    basicQuotePayload = this.basicQuotePayload() as BasicQuoteRequest,
    products = this.products(),
    agentDataStore = this.processTypeDataStore,
    selectedProductId = this.selectedProductId(),
    totalPrice = this.totalPrice()
  ): MakeQuoteBodyRequest {
    /* there is no destination type field */
    const body = new MakeQuoteBodyRequest();
    body.setDestinations(basicQuotePayload.destinations);
    body.setPolicyDestinationType(basicQuotePayload.policyDestinationType);
    body.setType(basicQuotePayload.type);
    body.setFromDate(basicQuotePayload.fromDate);
    body.setToDate(basicQuotePayload.toDate);
    body.setCalculatedPrice(totalPrice);
    body.setSelectInsuredAmountId(this.getAmount(products.insuredAmountData));
    body.setPayeeId(0);
    body.setCompanyId(0);
    body.setAreaId(0);
    body.setAppPartnersId(0);
    body.setAssociationId(agentDataStore.organizationId() || null);
    body.setAgentSaleNumberId(agentDataStore.agentSaleNumberDetails().id || null);
    body.setSiteUserId(agentDataStore.siteUserId() || null);
    body.setAgentWiseLeadId('string');
    body.setPolicyGroupId(selectedProductId || 0);
    body.setExcessLevelId(this.getLevel(products.excessLevels));
    body.setAmtTripDurationId(basicQuotePayload.amtTripDurationId || 0);
    body.setCfarApproval(basicQuotePayload.cfarApproval || false);
    body.setLeadId(''); //TODO: add leadId to payload after backend fix.
    body.setCouponId(0);
    body.setBillingCurrencyISO('AUD');
    body.setCampaignId(0);
    body.setIsMobile(true);
    body.setAccumulationAreaId(0);
    body.setMainCountryId(0);
    //body.setIsAnnual(true);
    body.setReferredBy(this.mainStore.getReferredById());
    body.setSingleTripDuration(0);
    body.setIsBusinessClass(true);
    body.setUwRequired(false);
    body.setDeclaredCovid19Risk(true);
    body.setDistributorUserId(basicQuotePayload.distributorUserId || '');
    body.setStateId(basicQuotePayload.stateId);
    body.setBrokerFee(agentDataStore.brokerFee());
    body.setBasicQuoteDetailsId(agentDataStore.basicQuoteDetailsId());
    body.setSite(0);
    body.setChannel(0);
    const travelers = this.mapTravelers(allTravelers, selectedProductId);
    body.setTravelers(travelers);
    body.setQuestionnaireId(this.productStore.questionnaireId() || 0);
    return body;
  }

  public getLeadId(): string | null {
    return this.processTypeDataStore.leadId();
  }

  private mapTravelers(allTravelers: Traveler[], selectedProductId?: number): Omit<Traveler, 'productCover'>[] {
    return allTravelers.map((traveler) => {
      const { productCover, ...rest } = traveler;
      return {
        ...rest,
        covers: this.mapCovers(productCover, selectedProductId)
      } as Omit<Traveler, 'productCover'>;
    });
  }

  private getAmount(insuredAmountData: InsuredAmount[]): number {
    const data = insuredAmountData.find((value) => value.IsSelected);
    return data ? data.ID : 0;
  }

  private getLevel(excessLevels: ExcessLevel[]): number {
    const data = excessLevels.find((value) => value.IsSelected);
    return data ? data.ID : 0;
  }

  private mapCovers(covers: ProductCover[] | null, selectedProductId: number | undefined): number[] {
    const coverIds = covers?.find((cover) => cover.productID === selectedProductId)?.coversIds || [];
    return coverIds;
  }

  private handlePreExCharge(participants: MakeQuoteParticipant[]): void {
    const uwTravelers = participants.filter((p) => p.uwResult === UWresultStatus.AutoRider);
    const extraPrice = this.addAutoRider(uwTravelers);

    this.showPreExChargePopup(extraPrice);
  }

  private addAutoRider(participants: MakeQuoteParticipant[]): number {
    let price = 0;
    const additionalCovers = [] as ICover[];
    const travelers = this.allTravelers();

    participants.forEach((participant) => {
      const travelerId = travelers.find((traveler) => traveler.participantNum === participant.participantNum)?.id;
      if (!travelerId) return;

      participant.covers.forEach((cover) => {
        const existingCover = additionalCovers.find((c) => c.id === cover.id);
        if (existingCover) {
          const existingTravelers = existingCover.travelers || [];
          const newTravelers = cover.travelers || [];
          existingCover.travelers = [...existingTravelers, ...newTravelers];
        } else {
          additionalCovers.push({ ...cover, coverType: CoverType.hidden });
        }

        this.travelersStore.addCoverToTraveler(this.selectedProductId(), cover.id, travelerId);
        price += cover.travelers?.[0]?.Price || 0;
      });
    });
    this.productStore.addCoversToProduct(this.selectedProductId(), additionalCovers);

    return price;
  }

  private showPreExChargePopup(extraChargePrice: number): void {
    const price = new CurrencyPipe('en-AU').transform(extraChargePrice, 'AUD', 'symbol-narrow', '1.2-2');

    this.animationDialogService.openDialogWithAnimation(this.dialog, AlertPopupComponent, {
      title: this.translations.instant(this.translations.preExCharge.title),
      information: this.translations.instant(this.translations.preExCharge.information, { price }),
      buttonText: this.translations.instant(this.translations.acceptAndContinue),
      mobileImgPath: PcAlertsImages.Mobile_Preex,
      desktopImgPath: PcAlertsImages.Desktop_Preex,
      imgAlt: 'the_preex'
    });
  }

  private showReferalPopup(): void {
    const servicePhone = this.translations.instant(this.translations.ReferralPopup.secondaryButtonText, {
      noSpacePhone: this.servicePhone().replace(/\s+/g, ''),
      phone: this.servicePhone()
    });

    const dialog = this.animationDialogService.openDialogWithAnimation(this.dialog, ReferralPopupComponent, {
      title: this.translations.ReferralPopup.titleDefault,
      titleAfterConfirmation: this.translations.ReferralPopup.titleAfterConfirmation,
      textInformation: this.translations.ReferralPopup.textInformation,
      textInformationAfterConfirmation: this.translations.ReferralPopup.textInformationAfterConfirmation,
      textSubInformation: this.translations.ReferralPopup.textSubInformation,
      textSubInformationAfterConfirmation: this.translations.ReferralPopup.textSubInformationAfterConfirmation,
      buttonTextAfterConfirmation: this.translations.ReferralPopup.buttonTextAfterConfirmation,
      buttonText: this.translations.ReferralPopup.buttonText,
      secondaryButtonText: this.domSanitizer.bypassSecurityTrustHtml(servicePhone),
      collapsibleCardTitle: this.translations.SuccessScreen.summaryOfCover,
      summaryTemplate: this.quoteOverviewService.getSummaryTemplateRef(),
      isCloseIcon: true,
      notes: this.exceptionMessage
    } as ReferralPopupData);

    dialog.closed.subscribe(() => {
      window.location.reload();
    });
  }

  private getServicePhoneNumber(isAgentProcess: boolean): string {
    const phone = this.translations.servicePhoneNumber;
    return this.translations.instant(isAgentProcess ? phone.agents : phone.regular);
  }

  private clearOutdatedCovers(): void {
    const selectedProduct = this.productStore.products().find((p) => p.policyGroupID === this.selectedProductId());

    if (selectedProduct) {
      this.travelersStore.allTravelers().forEach((traveler) => {
        const product = traveler.productCover.find((p) => selectedProduct.policyGroupID === p.productID);

        if (product) {
          product.coversIds = product.coversIds.filter((cid) =>
            selectedProduct.covers.some((cover) => cover.id === cid)
          );
        }
      });
    }
  }
}
